/**
 * service-printing client. Mirrors:
 *   apps/service-printing/src/modules/print-job/print-job.controller.ts
 *   apps/service-printing/src/modules/label-batch/label-batch.controller.ts
 *   apps/service-printing/src/modules/template/template.controller.ts
 *
 * Surface used by Phase 2b:
 *   - POST  /v1/printing/jobs            — queue a print job (label, receipt)
 *   - GET   /v1/printing/jobs            — list (filtered by job type)
 *   - GET   /v1/printing/jobs/:id        — fetch one
 *   - POST  /v1/printing/batches         — create a label batch
 *   - POST  /v1/printing/batches/:id/items — add items to a batch
 *   - POST  /v1/printing/batches/:id/render — generate the merged PDF
 *
 * Receipts: there is no `/v1/receipts/:id` endpoint today. The receipt-preview
 * screen renders the styled mock and surfaces that gap inline.
 */
import { env } from '../env';

import { request } from './client';

const baseUrl = (): string => env.servicePrintingBaseUrl;

export type PrintJobType = 'LABEL' | 'RECEIPT' | 'MANIFEST' | 'INVOICE';
export type PrintJobStatus =
  | 'QUEUED'
  | 'RENDERING'
  | 'READY'
  | 'PRINTING'
  | 'PRINTED'
  | 'FAILED'
  | 'CANCELLED';

export interface PrintJobSummary {
  readonly id: string;
  readonly tenantId: string;
  readonly type: PrintJobType;
  readonly status: PrintJobStatus;
  readonly templateId: string;
  readonly entityId?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ListPrintJobsResponse {
  readonly items: readonly PrintJobSummary[];
  readonly page: number;
  readonly pageSize: number;
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
}

export interface CreatePrintJobRequest {
  readonly type: PrintJobType;
  readonly templateId: string;
  readonly entityId?: string;
  readonly payload: Record<string, unknown>;
}

export interface LabelBatchSummary {
  readonly id: string;
  readonly tenantId: string;
  readonly status: PrintJobStatus;
  readonly itemCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export async function listPrintJobs(query: {
  type?: PrintJobType;
  status?: PrintJobStatus;
  page?: number;
  pageSize?: number;
} = {}): Promise<ListPrintJobsResponse> {
  return request<ListPrintJobsResponse>(baseUrl(), '/v1/printing/jobs', {
    method: 'GET',
    query: {
      type: query.type,
      status: query.status,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

export async function createPrintJob(dto: CreatePrintJobRequest): Promise<PrintJobSummary> {
  return request<PrintJobSummary>(baseUrl(), '/v1/printing/jobs', {
    method: 'POST',
    body: dto,
  });
}

export async function getPrintJob(id: string): Promise<PrintJobSummary> {
  return request<PrintJobSummary>(baseUrl(), `/v1/printing/jobs/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

export async function createLabelBatch(): Promise<LabelBatchSummary> {
  return request<LabelBatchSummary>(baseUrl(), '/v1/printing/batches', {
    method: 'POST',
    body: {},
  });
}

export async function addLabelBatchItems(
  id: string,
  items: readonly { entityId: string; payload: Record<string, unknown> }[],
): Promise<LabelBatchSummary> {
  return request<LabelBatchSummary>(
    baseUrl(),
    `/v1/printing/batches/${encodeURIComponent(id)}/items`,
    { method: 'POST', body: { items } },
  );
}

export async function renderLabelBatch(id: string): Promise<LabelBatchSummary> {
  return request<LabelBatchSummary>(
    baseUrl(),
    `/v1/printing/batches/${encodeURIComponent(id)}/render`,
    { method: 'POST', body: {} },
  );
}

// ─── Printers (service-printing /v1/printing/printers) ────────────────────

export type PrinterKind = 'THERMAL_4x6' | 'THERMAL_88MM' | 'LASER_A4' | 'INKJET_A4';
export type PrinterStatus = 'ONLINE' | 'OFFLINE' | 'ERROR' | 'MAINTENANCE';

export interface PrinterSummary {
  readonly id: string;
  readonly tenantId: string;
  readonly operatorId: number;
  readonly printerName: string;
  readonly kind: PrinterKind;
  readonly endpoint: string;
  readonly status: PrinterStatus;
  readonly lastHeartbeatAt: string | null;
  readonly diagnostics: unknown;
  readonly active: boolean;
}

export interface ListPrintersResponse {
  readonly items: readonly PrinterSummary[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export async function listPrinters(query: {
  readonly status?: PrinterStatus;
  readonly page?: number;
  readonly pageSize?: number;
} = {}): Promise<ListPrintersResponse> {
  return request<ListPrintersResponse>(baseUrl(), '/v1/printing/printers', {
    method: 'GET',
    query: { status: query.status, page: query.page, pageSize: query.pageSize },
  });
}

export async function getPrinter(id: string): Promise<PrinterSummary> {
  return request<PrinterSummary>(
    baseUrl(),
    `/v1/printing/printers/${encodeURIComponent(id)}`,
    { method: 'GET' },
  );
}

// ─── Templates (service-printing /v1/printing/templates) ──────────────────

export interface PrintTemplateSummary {
  readonly id: string;
  readonly tenantId: string;
  readonly templateKey: string;
  readonly version: number;
  readonly kind: string;
  readonly htmlBody: string;
  readonly variablesSchema: unknown;
  readonly active: boolean;
  readonly description: string | null;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ListTemplatesResponse {
  readonly items: readonly PrintTemplateSummary[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export async function listPrintTemplates(query: {
  readonly kind?: string;
  readonly active?: boolean;
  readonly page?: number;
  readonly pageSize?: number;
} = {}): Promise<ListTemplatesResponse> {
  return request<ListTemplatesResponse>(baseUrl(), '/v1/printing/templates', {
    method: 'GET',
    query: {
      kind: query.kind,
      active: query.active,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

export async function getPrintTemplate(id: string): Promise<PrintTemplateSummary> {
  return request<PrintTemplateSummary>(
    baseUrl(),
    `/v1/printing/templates/${encodeURIComponent(id)}`,
    { method: 'GET' },
  );
}

export interface CreatePrintTemplateRequest {
  readonly templateKey: string;
  readonly kind: string;
  readonly htmlBody: string;
  readonly variablesSchema: { type: 'object'; properties: Record<string, unknown>; required?: string[] };
  readonly description?: string;
}

export async function createPrintTemplate(dto: CreatePrintTemplateRequest): Promise<PrintTemplateSummary> {
  return request<PrintTemplateSummary>(baseUrl(), '/v1/printing/templates', {
    method: 'POST',
    body: dto,
  });
}

export interface UpdatePrintTemplateRequest {
  readonly htmlBody?: string;
  readonly variablesSchema?: { type: 'object'; properties: Record<string, unknown> };
  readonly description?: string;
}

export async function updatePrintTemplate(
  id: string,
  dto: UpdatePrintTemplateRequest,
): Promise<PrintTemplateSummary> {
  return request<PrintTemplateSummary>(
    baseUrl(),
    `/v1/printing/templates/${encodeURIComponent(id)}`,
    { method: 'PUT', body: dto },
  );
}

export interface PreviewTemplateResponse {
  /** Rendered HTML output (field name from the service is `rendered`). */
  readonly rendered: string;
  readonly ok: boolean;
  readonly missingVariables: string[];
}

export async function previewPrintTemplate(
  id: string,
  variables: Record<string, unknown>,
): Promise<PreviewTemplateResponse> {
  return request<PreviewTemplateResponse>(
    baseUrl(),
    `/v1/printing/templates/${encodeURIComponent(id)}/preview`,
    { method: 'POST', body: { variables } },
  );
}

// ─── Printer mutations (service-printing /v1/printing/printers) ────────────

export interface RegisterPrinterRequest {
  readonly printerName: string;
  readonly kind: 'THERMAL_4x6' | 'THERMAL_88MM' | 'LASER_A4' | 'INKJET_A4';
  readonly endpoint: string;
  readonly initialStatus?: 'OFFLINE' | 'ONLINE';
}

export async function registerPrinter(dto: RegisterPrinterRequest): Promise<PrinterSummary> {
  return request<PrinterSummary>(baseUrl(), '/v1/printing/printers', {
    method: 'POST',
    body: dto,
  });
}

export async function setPrinterStatus(
  id: string,
  status: PrinterStatus,
  reason: string,
): Promise<PrinterSummary> {
  return request<PrinterSummary>(
    baseUrl(),
    `/v1/printing/printers/${encodeURIComponent(id)}/status`,
    { method: 'POST', body: { status, reason } },
  );
}
